import React from "react";

import { Pencil, Trash2 } from "lucide-react";

import { AdvertisingEnum } from "@/src/types/advertisings";

import DataTableColumnHeader from "../../dataTable/DataTableColumnHeader";
import {
  actionsColumn,
  dateColumn,
  statusColumn,
} from "../../dataTable/dataTableColumns";
import DataTableLinkCell from "../../dataTable/DataTableLinkCell";
import { CELL_ICON_CLASS } from "../../dataTable/dataTableStyles";
import { advertisingPath } from "../../routes/routes";
import { ADVERTISING_TYPE_ICONS } from "../advertisingTypeIcons";

import type { TFunction } from "i18next";
import type { LucideIcon } from "lucide-react";

import type { AdvertisingRow } from "./advertisingColumns.types";
import type { DataTableColumnDef } from "../../dataTable/dataTable.types";

interface AdvertisingColumnsOptions {
  t: TFunction;
  projectSlug: string;
  onEdit: (advertising: AdvertisingRow) => void;
  onDelete: (advertising: AdvertisingRow) => void;
}

const ADVERTISING_TYPE_LIST = [
  AdvertisingEnum.CSAI_MP4,
  AdvertisingEnum.CSAI_VAST,
] as const;

export function advertisingColumns({
  t,
  projectSlug,
  onEdit,
  onDelete,
}: AdvertisingColumnsOptions): DataTableColumnDef<AdvertisingRow>[] {
  return [
    statusColumn<AdvertisingRow>("status", t("status"), [
      {
        value: "enabled",
        label: t("table:status.enabled"),
        tone: "success",
      },
      {
        value: "paused",
        label: t("table:status.paused"),
        tone: "warning",
      },
      {
        value: "disabled",
        label: t("table:status.disabled"),
        tone: "error",
      },
      {
        value: "unknown",
        label: t("table:status.unknown"),
        tone: "neutral",
      },
    ]),
    {
      accessorKey: "type",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("type")} />
      ),
      enableSorting: false,
      meta: {
        label: t("type"),
        width: "12%",
        minWidth: 115,
        filterOptions: ADVERTISING_TYPE_LIST.map((type) => {
          const TypeIcon = ADVERTISING_TYPE_ICONS[type];
          return { value: type, label: t(type), icon: <TypeIcon /> };
        }),
      },
      cell: ({ row }) => {
        const TypeIcon: LucideIcon | undefined =
          ADVERTISING_TYPE_ICONS[row.original.type];
        return (
          <div className="inline-flex items-center gap-1 align-middle">
            {TypeIcon ? (
              <TypeIcon aria-hidden="true" className={CELL_ICON_CLASS} />
            ) : null}
            {t(row.original.type)}
          </div>
        );
      },
    },
    {
      accessorKey: "label",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("title")} />
      ),
      meta: { searchable: true },
      cell: ({ row }) => (
        <DataTableLinkCell
          href={advertisingPath(projectSlug, row.original.uuid)}
        >
          {row.original.label}
        </DataTableLinkCell>
      ),
    },
    {
      accessorKey: "offer_plans_count",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("relatedOffers")} />
      ),
      meta: {
        label: t("relatedOffers"),
        width: "15%",
        minWidth: 155,
        align: "center",
      },
    },
    dateColumn<AdvertisingRow>("updated_at", t("update_date"), {
      width: "12%",
      minWidth: 130,
    }),
    actionsColumn<AdvertisingRow>(
      (advertising) => [
        {
          label: t("edit"),
          icon: <Pencil />,
          onClick: () => onEdit(advertising),
        },
        {
          label: t("delete"),
          icon: <Trash2 />,
          variant: "destructive",
          onClick: () => onDelete(advertising),
          confirm: {
            title: t("deleteTitle"),
            description: t("deleteBody", { campaign: advertising.label }),
            confirmLabel: t("deleteConfirm"),
          },
        },
      ],
      (advertising) => advertising.label,
    ),
  ];
}
